import { useMemo } from 'react';
import {
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeterTypeInternal,
  ScalarParameterTypeInternal,
  VectorParameterTypeInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { useMeasurementMapOperations } from '../../../../../hooks/measurement-map/use-measurement-map-operations';
import { ChannelMeterType } from '../../../../../types';
import { useMeterDetailsContext } from '../../context';
import { CurrentHookParams } from '../types';
import { getParameterAverageValue, getParameterMaxValue, getParameterMinValue } from '../utils';
import { useMeasurements } from './use-measurements';

const kind = MeasurementChannelKind.Meter;

export const useMeterCurrent = ({ channel }: CurrentHookParams) => {
  const supportedScalars = (channel.data as ChannelMeterType).supportedScalarParameters;
  const { convertMeasurementToNumber } = useMeasurementMapOperations();
  const { currentParameterIndex, setCurrentParameterIndex, setCurrentPhases } = useMeterDetailsContext();
  const { parameters, data, vectorMeasurements, scalarMeasurements } = useMeasurements(channel);

  const selectedParameter = useMemo(() => {
    return parameters[currentParameterIndex].value;
  }, [currentParameterIndex, parameters]);

  const changeSelectedParameter = (value?: VectorParameterTypeInternal) => {
    switch (value) {
      case VectorParameterTypeInternal.ActivePower:
      case VectorParameterTypeInternal.Current:
        setCurrentPhases([0]);
        break;
      case VectorParameterTypeInternal.Voltage:
        setCurrentPhases([1]);
        break;
      default:
        break;
    }

    if (value && Object.values(VectorParameterTypeInternal).includes(value)) {
      const found = parameters.find((x) => x.value === value);
      if (found?.weight !== undefined) setCurrentParameterIndex(found.weight);

      return;
    }

    if (currentParameterIndex === parameters.length - 1) {
      setCurrentPhases([0]);
      setCurrentParameterIndex(0);
    } else {
      const newIndex = currentParameterIndex + 1;

      const param = parameters[newIndex].value;
      setCurrentParameterIndex(newIndex);

      if (param === VectorParameterTypeInternal.Voltage) {
        setCurrentPhases([1]);
      }
    }
  };

  const totalForwardActiveEnergy = useMemo(() => {
    return (
      (vectorMeasurements || []).filter(
        (measurement) =>
          measurement.type ===
          convertMeasurementToNumber(kind)('vectors', VectorParameterTypeInternal.ForwardActiveEnergy),
      ) || []
    );
  }, [vectorMeasurements]);

  const selectedAverageValue = useMemo(() => {
    return (
      (scalarMeasurements || []).find((measurement) => {
        const scalarVal = getParameterAverageValue(selectedParameter);

        return scalarVal ? measurement.type === convertMeasurementToNumber(kind)('scalars', scalarVal) : 0;
      })?.value ?? 0
    );
  }, [scalarMeasurements, selectedParameter]);

  const selectedMaxValue = useMemo(() => {
    return (
      (scalarMeasurements || []).find((measurement) => {
        const scalarVal = getParameterMaxValue(selectedParameter);

        return scalarVal ? measurement.type === convertMeasurementToNumber(kind)('scalars', scalarVal) : 0;
      })?.value ?? 0
    );
  }, [scalarMeasurements, selectedParameter]);

  const selectedMinValue = useMemo(() => {
    return (
      (scalarMeasurements || []).find((measurement) => {
        const scalarVal = getParameterMinValue(selectedParameter);

        return scalarVal ? measurement.type === convertMeasurementToNumber(kind)('scalars', scalarVal) : 0;
      })?.value ?? 0
    );
  }, [scalarMeasurements, selectedParameter]);

  const ecoIndicator = useMemo(() => {
    return (
      (scalarMeasurements || []).find(
        (measurement) =>
          measurement.type === convertMeasurementToNumber(kind)('scalars', ScalarParameterTypeInternal.EcoIndicator),
      )?.value || null
    );
  }, [scalarMeasurements]);

  const selectedSinglePhaseMeasurements = useMemo(() => {
    return (vectorMeasurements || []).filter(
      (measurement) => measurement.type === convertMeasurementToNumber(kind)('vectors', selectedParameter),
    );
  }, [vectorMeasurements, selectedParameter]);

  const isActivePowerSelected = useMemo(() => {
    return selectedParameter === VectorParameterTypeInternal.ActivePower;
  }, [selectedParameter]);

  const isStandalone =
    channel.data.type === ChannelTypeInternal.Meter && channel.data.meterType === MeterTypeInternal.Standalone;

  const supportedScalarMin = useMemo(
    () =>
      supportedScalars.includes(
        convertMeasurementToNumber(MeasurementChannelKind.Meter)('scalars', getParameterMinValue(selectedParameter)),
      ),
    [supportedScalars, convertMeasurementToNumber, selectedParameter],
  );

  const supportedScalarMax = useMemo(
    () =>
      supportedScalars.includes(
        convertMeasurementToNumber(MeasurementChannelKind.Meter)('scalars', getParameterMaxValue(selectedParameter)),
      ),
    [supportedScalars, convertMeasurementToNumber, selectedParameter],
  );

  const supportedScalarAverage = useMemo(
    () =>
      supportedScalars.includes(
        convertMeasurementToNumber(MeasurementChannelKind.Meter)(
          'scalars',
          getParameterAverageValue(selectedParameter),
        ),
      ),
    [supportedScalars, convertMeasurementToNumber, selectedParameter],
  );

  return {
    data,
    totalForwardActiveEnergy,
    selectedAverageValue,
    selectedMaxValue,
    selectedMinValue,
    ecoIndicator,
    selectedParameter,
    changeSelectedParameter,
    isActivePowerSelected,
    selectedSinglePhaseMeasurements,
    isStandalone,
    parameters,
    supportedScalarMin,
    supportedScalarMax,
    supportedScalarAverage,
  };
};
